import { createI18n } from 'vue-i18n'
import { locale } from './locales'

const FALLBACK_LOCALE = 'en-US'
const DEFAULT_MODULE = 'frames'
const ARIA_MODULE = 'aria-label'

const localeFiles = import.meta.glob('../locales/*/*.json', {
  eager: true,
  import: 'default'
})

function getLocaleCatalog(localeCode, moduleName = DEFAULT_MODULE) {
  const match = Object.entries(localeFiles).find(([filePath]) =>
    filePath.endsWith(`/${moduleName}/${localeCode}.json`)
  )
  return match?.[1] ?? null
}

function pickNamespace(catalog, namespace) {
  if (!catalog) return {}
  if (!namespace) return catalog
  return (
    namespace
      .split('.')
      .reduce((current, key) => (current == null ? current : current[key]), catalog) ?? {}
  )
}

function deepMerge(target, source) {
  if (!source) return target || {}
  if (!target) return source
  const merged = { ...target }
  for (const key of Object.keys(source)) {
    const next = source[key]
    const current = merged[key]
    if (
      current &&
      next &&
      typeof current === 'object' &&
      typeof next === 'object' &&
      !Array.isArray(current) &&
      !Array.isArray(next)
    ) {
      merged[key] = deepMerge(current, next)
    } else {
      merged[key] = next
    }
  }
  return merged
}

function buildNamespaceMessages(localeCode, namespace, moduleName) {
  const catalog = pickNamespace(getLocaleCatalog(localeCode, moduleName), namespace)
  if (moduleName !== DEFAULT_MODULE) return catalog
  const ariaCatalog = pickNamespace(getLocaleCatalog(localeCode, ARIA_MODULE), namespace)
  return deepMerge(catalog, { aria: ariaCatalog })
}

export function createAppI18n(namespace, options = {}) {
  const { module: moduleName = DEFAULT_MODULE, ...i18nOptions } = options
  const messages = {
    [FALLBACK_LOCALE]: buildNamespaceMessages(FALLBACK_LOCALE, namespace, moduleName)
  }

  if (locale !== FALLBACK_LOCALE) {
    messages[locale] = buildNamespaceMessages(locale, namespace, moduleName)
  }

  return createI18n({
    locale,
    fallbackLocale: FALLBACK_LOCALE,
    legacy: false,
    messageCompiler: false,
    fallbackWarn: false,
    messages,
    ...i18nOptions
  })
}

export async function setupI18n(namespace, options = {}) {
  return createAppI18n(namespace, options)
}
